import React from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";

/**
 * A bottom sheet that rises over the player to show overflow content in
 * full (e.g. a long trivia explanation). Portaled into the phone-frame
 * element (`#swipe-deck-frame`) — outside the deck's swipe viewport — so its
 * scroll/touch never collides with the deck's swipe gesture, while staying
 * confined to the phone preview. Falls back to <body> if the frame isn't
 * found.
 *
 * `data-deck-sheet="open"` is the flag SwipeDeckPlayer's key handler checks
 * to suppress its own hotkeys while a sheet is open, so Arrow keys scroll
 * the sheet instead of advancing the deck. Pass `scrollRef` to receive the
 * scroll region (focus it on open so keyboard scrolling works).
 *
 * Mirrors the inline sheet in ImageTextCard so the two read the same.
 */
export function CardExpandSheet({ open, onClose, title, scrollRef, children }) {
  const target =
    typeof document !== "undefined"
      ? document.getElementById("swipe-deck-frame") || document.body
      : null;
  if (!open || !target) return null;
  return createPortal(
    <div
      data-deck-sheet="open"
      className="absolute inset-0 z-30 flex items-end justify-center"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative flex flex-col w-full max-h-[85%] bg-white rounded-t-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 leading-snug">
              {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}
        <div
          ref={scrollRef}
          tabIndex={0}
          className="overflow-y-auto px-6 py-4 text-base text-gray-700 leading-relaxed break-words whitespace-pre-wrap focus:outline-none"
          style={{ touchAction: "pan-y" }}
        >
          {children}
        </div>
      </div>
    </div>,
    target,
  );
}
